import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { interval, Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { environment } from '@client/environments/environment';

import { NotificationService } from './notification.service';

const APP_VERSION_CHECK_INTERVAL_MILLIS = 900_000;

interface AppVersion {
  version: string;
}

@Injectable()
export class AppUpdateService {
  #http = inject(HttpClient);
  #notificationService = inject(NotificationService);
  #destroyRef = inject(DestroyRef);

  readonly #versionUrl = 'version.json';

  checkForUpdates(): void {
    interval(APP_VERSION_CHECK_INTERVAL_MILLIS)
      .pipe(
        switchMap(() => this.getAppVersion().pipe(map(({ version }) => version))),
        takeUntilDestroyed()
      )
      .subscribe(version => {
        if (version !== environment.app_version && this.#notificationService.showNotification$.getValue() === false) {
          this.#notificationService?.showNotification$.next(true);
          this.#refreshAtMidnight();
        }
      });
  }

  getAppVersion(): Observable<AppVersion> {
    return this.#http.get<AppVersion>(this.#versionUrl);
  }

  #refreshAtMidnight(): void {
    const timeUntilMidnight = this.#getTimeUntilMidnight();

    timer(timeUntilMidnight)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        document.location.reload();
      });
  }

  #getTimeUntilMidnight(): number {
    const now = new Date();
    const midnight = new Date();

    midnight.setHours(23, 59, 59, 999);

    return midnight.getTime() - now.getTime();
  }
}
